/**
 * Statement of changes in equity. IAS 1 paragraph 106, which the template has
 * nothing like.
 *
 * Entirely computed. Every figure was already in the model — share capital,
 * the reserves, other comprehensive income, retained earnings, dividends, the
 * share-based payment charge — so what this adds is not arithmetic but the
 * question the balance sheet cannot answer: equity fell by 3,000,000, and was
 * that a loss, a dividend, or a translation reserve moving? Two of those are
 * the owners' decision and one is the business.
 *
 * One block per component, each reconciling opening to closing. Dividends are
 * held positive in the model and shown as a deduction here.
 *
 * "Other movements" is a residual, not a cause. Where a filing states a
 * component's closing balance the engine honours it, and the difference has no
 * cause to name; a nil residual — the ordinary case — is the evidence that
 * nothing is unexplained.
 */

import { ComputedDataRow } from '../table/DataRow';
import { GrandTotalRow } from '../table/GrandTotalRow';
import { SectionHeader } from '../table/SectionHeader';
import { SubtotalRow } from '../table/SubtotalRow';
import { useFinancialModelV2Store } from '../../../store/financial-model-v2';
import type {
  EquityComponentComputed,
  PeriodData,
} from '../../../types/financial-model-v2';
import { V2Sheet } from './V2Table';

/** Same series with every figure negated, for a movement shown as a deduction. */
function negated(data: PeriodData): PeriodData {
  return Object.fromEntries(
    Object.entries(data ?? {}).map(([period, value]) => [period, -value]),
  );
}

function hasAny(data: PeriodData | undefined): boolean {
  return Object.values(data ?? {}).some((v) => v);
}

function ComponentBlock({
  component,
  total,
}: {
  component: EquityComponentComputed;
  total?: boolean;
}) {
  const movements: [string, PeriodData | undefined][] = [
    ['Profit for the period', component.profit],
    ['Other comprehensive income', component.otherComprehensiveIncome],
    ['Shares issued', component.sharesIssued],
    ['Dividends', negated(component.dividends)],
    ['Share-based payments', component.shareBasedPayments],
    ['Other movements', component.other],
  ];

  return (
    <>
      <SectionHeader label={component.caption} />
      <ComputedDataRow label="Opening" data={component.opening} />
      {movements
        .filter(([, data]) => hasAny(data))
        .map(([label, data]) => (
          <ComputedDataRow
            key={label}
            label={label}
            hint={{
              how: 'The movement in this part of equity in the year: profit and other comprehensive income from the P&L, shares issued and dividends from the Control Account, and any other movement the filing’s equity statement prints.',
              change: 'The P&L, the Control Account’s equity section, or the opening position on Settings.',
            }}
            data={data ?? {}}
          />
        ))}
      {total ? (
        <GrandTotalRow label="Closing" data={component.closing} />
      ) : (
        <SubtotalRow label="Closing" data={component.closing} />
      )}
    </>
  );
}

export function EquityViewV2() {
  const model = useFinancialModelV2Store((s) => s.model);
  if (!model) return null;
  const statement = model.computed.equityStatement;
  if (!statement) return null;

  return (
    <V2Sheet>
      {statement.components.map((component) => (
        <ComponentBlock key={component.caption} component={component} />
      ))}
      <ComponentBlock component={statement.total} total />
    </V2Sheet>
  );
}
